import { useCallback, useEffect, useRef, useState, Suspense, lazy, Component, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useAssistantConversation } from "../../../modules/assistant/state/AssistantConversationProvider";
import { ChatThread } from "../../../modules/assistant/components/ChatThread";
import { useAssistantConfig } from "../useAssistantConfig";
import { nextEntrancePhase, type EntranceEvent, type EntrancePhase } from "./useEntrancePhase";

const MochiScene = lazy(() => import("../MochiScene"));

/** Translate+scale toward the corner the avatar is configured to dock in. */
const DOCK_TRANSFORM: Record<string, string> = {
  "bottom-right": "translate-x-[42vw] translate-y-[38vh] scale-[0.25]",
  "bottom-left": "-translate-x-[42vw] translate-y-[38vh] scale-[0.25]",
  "top-right": "translate-x-[42vw] -translate-y-[38vh] scale-[0.25]",
  "top-left": "-translate-x-[42vw] -translate-y-[38vh] scale-[0.25]",
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Swallows a failed/throwing MochiScene import so a GPU/WebGL problem never
 *  blocks the entrance's digest + "Let's go" from being usable. */
class SceneBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? null : this.props.children;
  }
}

const DOCK_FALLBACK_MS = 600;
const DWELL_MS = 1200;

/**
 * Center-stage "while you were away" surface. Mounted by `MochiOverlay` when
 * `weco:mochi:enter-center` fires. Shows an enlarged Mochi + the live digest,
 * then docks itself into the configured corner and hands off to the normal
 * corner overlay via `onDocked` — the SAME conversation keeps streaming
 * throughout, nothing is restarted here.
 *
 * NOTE: the automatic post-login digest that dispatched `enter-center` was
 * removed (users asked for the summary popup not to appear on every login),
 * so this surface is currently dormant — kept as reusable center-stage
 * plumbing for any future code that dispatches `weco:mochi:enter-center`.
 */
export function MochiEntrance({ onDocked }: { onDocked: () => void }) {
  const { t } = useTranslation();
  const chat = useAssistantConversation();
  const cfg = useAssistantConfig();
  const [phase, setPhase] = useState<EntrancePhase>("idle");
  const cardRef = useRef<HTMLDivElement | null>(null);

  const dispatch = useCallback((ev: EntranceEvent) => {
    setPhase((p) => nextEntrancePhase(p, ev));
  }, []);

  // Enter on mount; reduced motion skips straight to docked (no animation,
  // no dwell) — both updates are functional, so they apply in order even
  // though they're dispatched in the same effect tick.
  useEffect(() => {
    dispatch("enter");
    if (prefersReducedMotion()) dispatch("reducedMotion");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dwell timer: once the digest has finished streaming while centered, give
  // the user a beat to read it before auto-docking.
  useEffect(() => {
    if (phase !== "center" || chat.status !== "idle") return;
    const id = window.setTimeout(() => dispatch("dwellElapsed"), DWELL_MS);
    return () => window.clearTimeout(id);
  }, [phase, chat.status, dispatch]);

  // Escape dismisses the same way "Let's go" does.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dispatch("letsGo");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dispatch]);

  // Docking -> dockDone on whichever comes first: a real transitionend, or a
  // fallback timer — so a missing/short-circuited transition can never
  // strand the entrance mid-dock.
  useEffect(() => {
    if (phase !== "docking") return;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      dispatch("dockDone");
    };
    const el = cardRef.current;
    el?.addEventListener("transitionend", finish);
    const timer = window.setTimeout(finish, DOCK_FALLBACK_MS);
    return () => {
      el?.removeEventListener("transitionend", finish);
      window.clearTimeout(timer);
    };
  }, [phase, dispatch]);

  // Docked: hand off to the corner overlay, which resumes the same conversation.
  useEffect(() => {
    if (phase === "docked") onDocked();
  }, [phase, onDocked]);

  if (phase === "idle" || phase === "docked") return null;

  const dockTransform = DOCK_TRANSFORM[cfg.avatar_placement] ?? DOCK_TRANSFORM["bottom-right"];
  const dockingClass = phase === "docking" ? `${dockTransform} opacity-0` : "";

  return (
    <div
      role="dialog"
      aria-label={t("assistant.mochi.entrance.backdropAria")}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/30 dark:bg-black/50"
    >
      <div
        ref={cardRef}
        className={`flex h-[min(640px,80vh)] w-[min(640px,92vw)] flex-col overflow-hidden rounded-3xl border border-border bg-background shadow-2xl transition-all duration-500 ease-in-out ${dockingClass}`}
        style={{ pointerEvents: "auto" }}
      >
        <div className="flex shrink-0 items-center justify-center py-6">
          <SceneBoundary>
            <Suspense fallback={<div className="size-40" />}>
              <MochiScene onClick={() => dispatch("letsGo")} avatar={cfg.avatar} palette={cfg.palette} size="lg" />
            </Suspense>
          </SceneBoundary>
        </div>
        <ChatThread messages={chat.messages} errorMessage={chat.errorMessage} onRetry={chat.retry} />
        <div className="flex shrink-0 justify-center px-4 pb-4">
          <button
            type="button"
            onClick={() => dispatch("letsGo")}
            className="rounded-full bg-primary px-6 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
          >
            {t("assistant.mochi.entrance.letsGo")}
          </button>
        </div>
      </div>
    </div>
  );
}
